import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Upload, Edit2, Trash2, X, DollarSign, Clock, AlertCircle, Users, RefreshCw } from 'lucide-react';
import { ProfitAnalysisService } from '../../services/profitAnalysisService';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const PayrollAdmin = ({ onDataChange }) => {
  const [payroll, setPayroll] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [trueCosts, setTrueCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddSaving, setQuickAddSaving] = useState(false);
  const [refreshingRates, setRefreshingRates] = useState(false);
  const fileInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    employeeId: '',
    payPeriodStart: '',
    payPeriodEnd: '',
    grossPay: '',
    notes: ''
  });

  // Quick Add form state
  const [quickAddData, setQuickAddData] = useState({
    payPeriodStart: '',
    payPeriodEnd: '',
    amounts: {} // { [employeeId]: amount }
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [payrollData, employeesData, trueCostsData] = await Promise.all([
        ProfitAnalysisService.getAllPayroll(),
        ProfitAnalysisService.getEmployees(),
        ProfitAnalysisService.calculateTrueCosts()
      ]);
      setPayroll(payrollData);
      setEmployees(employeesData);
      setTrueCosts(trueCostsData);
    } catch (err) {
      console.error('Error loading payroll data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshRates = async (silent = false) => {
    try {
      setRefreshingRates(true);
      const trueCostsData = await ProfitAnalysisService.calculateTrueCosts();
      setTrueCosts(trueCostsData);

      if (!silent) {
        alert('Rates reloaded. New rates are calculated automatically after each Patriot payroll upload.');
      }
    } catch (err) {
      console.error('Error refreshing rates:', err);
      if (!silent) {
        alert(`Error refreshing rates: ${err.message}`);
      }
    } finally {
      setRefreshingRates(false);
    }
  };

  const handleOpenModal = (record = null) => {
    if (record) {
      setEditingRecord(record);
      setFormData({
        employeeId: record.employeeId.toString(),
        payPeriodStart: record.payPeriodStart,
        payPeriodEnd: record.payPeriodEnd,
        grossPay: record.grossPay.toString(),
        notes: record.notes || ''
      });
    } else {
      setEditingRecord(null);
      setFormData({
        employeeId: '',
        payPeriodStart: '',
        payPeriodEnd: '',
        grossPay: '',
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRecord(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        employeeId: parseInt(formData.employeeId),
        payPeriodStart: formData.payPeriodStart,
        payPeriodEnd: formData.payPeriodEnd,
        grossPay: parseFloat(formData.grossPay),
        notes: formData.notes || null
      };

      if (editingRecord) {
        await ProfitAnalysisService.updatePayroll(editingRecord.id, data);
      } else {
        await ProfitAnalysisService.createPayroll(data);
      }

      handleCloseModal();
      loadData();
      if (onDataChange) onDataChange();
    } catch (err) {
      console.error('Error saving payroll:', err);
      alert(`Error saving: ${err.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payroll record?')) return;

    try {
      await ProfitAnalysisService.deletePayroll(id);
      loadData();
      if (onDataChange) onDataChange();
    } catch (err) {
      console.error('Error deleting payroll:', err);
      alert(`Error deleting: ${err.message}`);
    }
  };

  // Calculate next pay period based on most recent entry
  const suggestedPayPeriod = useMemo(() => {
    if (payroll.length === 0) {
      // Default to current 2-week period
      const today = new Date();
      const dayOfWeek = today.getDay();
      // Assume pay periods end on Saturday (day 6)
      const daysUntilSaturday = (6 - dayOfWeek + 7) % 7;
      const endDate = new Date(today);
      endDate.setDate(today.getDate() + daysUntilSaturday);
      const startDate = new Date(endDate);
      startDate.setDate(endDate.getDate() - 13); // 2 weeks
      return {
        start: startDate.toISOString().split('T')[0],
        end: endDate.toISOString().split('T')[0]
      };
    }

    // Find the most recent pay period end date
    const mostRecent = payroll.reduce((latest, p) =>
      !latest || p.payPeriodEnd > latest.payPeriodEnd ? p : latest
    , null);

    if (mostRecent) {
      // Next period starts the day after the most recent end
      const nextStart = new Date(mostRecent.payPeriodEnd + 'T00:00:00');
      nextStart.setDate(nextStart.getDate() + 1);
      const nextEnd = new Date(nextStart);
      nextEnd.setDate(nextStart.getDate() + 13); // 2 weeks
      return {
        start: nextStart.toISOString().split('T')[0],
        end: nextEnd.toISOString().split('T')[0]
      };
    }

    return { start: '', end: '' };
  }, [payroll]);

  // Quick Add handlers
  const handleOpenQuickAdd = () => {
    setQuickAddData({
      payPeriodStart: suggestedPayPeriod.start,
      payPeriodEnd: suggestedPayPeriod.end,
      amounts: {}
    });
    setShowQuickAdd(true);
  };

  const handleQuickAddAmountChange = (employeeId, value) => {
    setQuickAddData(prev => ({
      ...prev,
      amounts: {
        ...prev.amounts,
        [employeeId]: value
      }
    }));
  };

  const handleQuickAddSubmit = async (e) => {
    e.preventDefault();

    const entries = Object.entries(quickAddData.amounts)
      .filter(([_, amount]) => amount && parseFloat(amount) > 0);

    if (entries.length === 0) {
      alert('Please enter at least one amount');
      return;
    }

    try {
      setQuickAddSaving(true);
      let saved = 0;
      let errors = [];

      for (const [employeeId, amount] of entries) {
        try {
          await ProfitAnalysisService.createPayroll({
            employeeId: parseInt(employeeId),
            payPeriodStart: quickAddData.payPeriodStart,
            payPeriodEnd: quickAddData.payPeriodEnd,
            grossPay: parseFloat(amount),
            notes: 'Manual entry'
          });
          saved++;
        } catch (err) {
          const empName = employees.find(e => e.id === parseInt(employeeId))?.name || employeeId;
          errors.push(`${empName}: ${err.message}`);
        }
      }

      if (errors.length > 0) {
        alert(`Saved ${saved} entries.\n\nErrors:\n${errors.join('\n')}`);
      }

      setShowQuickAdd(false);
      loadData();
      if (onDataChange) onDataChange();
    } catch (err) {
      console.error('Error saving quick add:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setQuickAddSaving(false);
    }
  };

  // CSV Import handlers
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      if (typeof text === 'string') {
        parseCSV(text);
      }
    };
    reader.readAsText(file);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n');
    if (lines.length < 2) {
      alert('CSV file is empty or invalid');
      return;
    }

    // Parse header row - handle quoted values
    const parseRow = (line) => {
      const values = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      return values;
    };

    const headers = parseRow(lines[0]);

    // Map header names to our expected keys
    const headerMap = {};
    headers.forEach((h, i) => {
      const lower = h.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (lower.includes('payperiod')) headerMap.payPeriod = i;
      else if (lower.includes('employeefirst')) headerMap.employeeFirstName = i;
      else if (lower.includes('employeelast')) headerMap.employeeLastName = i;
      else if (lower === 'grosspay') headerMap.grossPay = i;
      else if (lower.includes('employermedicare')) headerMap.employerMedicareTax = i;
      else if (lower.includes('employersocialsecurity')) headerMap.employerSocialSecurity = i;
      else if (lower.includes('source')) headerMap.source = i;
      else if (lower.includes('unemployment')) headerMap.stateUnemployment = i;
    });

    // Parse data rows
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = parseRow(line);
      const row = {
        payPeriod: values[headerMap.payPeriod] || '',
        employeeFirstName: values[headerMap.employeeFirstName] || '',
        employeeLastName: values[headerMap.employeeLastName] || '',
        grossPay: values[headerMap.grossPay] || '0',
        employerMedicareTax: values[headerMap.employerMedicareTax] || '0',
        employerSocialSecurity: values[headerMap.employerSocialSecurity] || '0',
        source: values[headerMap.source] || '',
        stateUnemployment: values[headerMap.stateUnemployment] || '0'
      };

      // Skip voided or empty rows
      if (row.source.toLowerCase().includes('void')) continue;
      if (!row.employeeFirstName && !row.employeeLastName) continue;

      rows.push(row);
    }

    setImportPreview({
      totalRows: rows.length,
      rows: rows.slice(0, 10), // Preview first 10
      allRows: rows
    });
    setShowImportModal(true);
  };

  const handleImport = async () => {
    if (!importPreview?.allRows) return;

    try {
      setImporting(true);
      const results = await ProfitAnalysisService.importPayrollFromCSV(
        importPreview.allRows,
        employees
      );

      alert(`Import complete!\n\nImported: ${results.imported}\nSkipped: ${results.skipped}${results.errors.length > 0 ? `\n\nErrors:\n${results.errors.slice(0, 5).join('\n')}${results.errors.length > 5 ? `\n... and ${results.errors.length - 5} more` : ''}` : ''}`);

      setShowImportModal(false);
      setImportPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadData();
      if (onDataChange) onDataChange();
    } catch (err) {
      console.error('Error importing payroll:', err);
      alert(`Import error: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="compensation-admin">
        <div className="loading-container">
          <p>Loading payroll data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="compensation-admin">
        <div className="empty-state">
          <h3>Error loading payroll data</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Get all employees with true cost data (either payroll or hours data)
  const employeesWithCosts = trueCosts.filter(tc => tc.hasPayrollData || tc.hasHoursData);

  return (
    <div className="compensation-admin">
      {/* Header */}
      <div className="compensation-header">
        <div className="compensation-title">
          <DollarSign size={24} color="var(--interactive-primary)" />
          <h2>Labor Costs</h2>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <button
            className="add-rate-btn"
            onClick={() => fileInputRef.current?.click()}
            style={{ background: 'var(--surface-tertiary)', color: 'var(--text-primary)' }}
          >
            <Upload size={18} />
            Import CSV
          </button>
          <button className="add-rate-btn" onClick={handleOpenQuickAdd}>
            <Users size={18} />
            Quick Add
          </button>
        </div>
      </div>

      {/* True Cost Summary Cards */}
      {employeesWithCosts.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
              <Users size={16} />
              Project Cost Rates
            </h4>
            <button
              onClick={() => handleRefreshRates(false)}
              disabled={refreshingRates}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.375rem 0.75rem',
                fontSize: '0.75rem',
                background: 'var(--surface-tertiary)',
                border: '1px solid var(--border-primary)',
                borderRadius: '0.375rem',
                color: 'var(--text-secondary)',
                cursor: refreshingRates ? 'not-allowed' : 'pointer',
                opacity: refreshingRates ? 0.6 : 1
              }}
            >
              <RefreshCw size={14} className={refreshingRates ? 'animate-spin' : ''} />
              {refreshingRates ? 'Reloading...' : 'Reload Rates'}
            </button>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1rem'
          }}>
            {employeesWithCosts.map((tc) => (
              <div
                key={tc.employeeId}
                style={{
                  background: 'var(--surface-secondary)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '0.5rem',
                  padding: '1rem'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                  <h5 style={{ fontWeight: 600, fontSize: '0.9375rem', margin: 0 }}>{tc.employeeName}</h5>
                  {tc.trueCostPerHour !== null ? (
                    <span style={{
                      fontSize: '1.125rem',
                      fontWeight: 700,
                      color: 'var(--interactive-primary)'
                    }}>
                      {formatCurrency(tc.trueCostPerHour)}/hr
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>
                      No data
                    </span>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.8125rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'var(--text-secondary)' }}>
                    <DollarSign size={14} />
                    <span>Total Pay:</span>
                  </div>
                  <div style={{ textAlign: 'right', fontWeight: 500 }}>
                    {formatCurrency(tc.totalGrossPay)}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'var(--text-secondary)' }}>
                    <Clock size={14} />
                    <span>Project Hrs:</span>
                  </div>
                  <div style={{ textAlign: 'right', fontWeight: 500 }}>
                    {tc.totalProjectHours?.toFixed(1) || '0.0'}
                  </div>
                </div>

                {tc.calculationMethod && (
                  <div style={{
                    marginTop: '0.5rem',
                    fontSize: '0.75rem',
                    color: 'var(--text-tertiary)',
                    fontStyle: 'italic'
                  }}>
                    {tc.calculationMethod}
                  </div>
                )}

                {!tc.hasPayrollData && (
                  <div style={{
                    marginTop: '0.75rem',
                    padding: '0.5rem',
                    background: 'var(--status-warning-bg)',
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    color: 'var(--status-warning)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem'
                  }}>
                    <AlertCircle size={14} />
                    No payroll data for this period
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div style={{
        background: 'var(--surface-tertiary)',
        border: '1px solid var(--border-primary)',
        borderRadius: '0.5rem',
        padding: '1rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        fontSize: '0.875rem',
        color: 'var(--text-secondary)',
        marginBottom: '1rem'
      }}>
        <DollarSign size={20} style={{ flexShrink: 0, marginTop: '0.125rem' }} />
        <div>
            <strong style={{ color: 'var(--text-primary)' }}>Project Cost Calculation:</strong>
          <p style={{ margin: '0.25rem 0 0' }}>
            Current compensation + actual employer taxes + 0.30% workers&apos; comp,
            normalized for PTO and holidays, divided by 26-week project utilization.
            Five hours per week remain company overhead and are not charged to projects.
          </p>
        </div>
      </div>

      {/* Payroll Table */}
      <div className="compensation-table-section">
        <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
          Payroll Entries ({payroll.length})
        </h4>
        <table className="compensation-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Pay Period</th>
              <th>Total Cost</th>
              <th>Notes</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {payroll.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                  No payroll entries. Import from Gusto CSV or add entries manually.
                </td>
              </tr>
            ) : (
              payroll.slice(0, 50).map((record) => (
                <tr key={record.id}>
                  <td style={{ fontWeight: 500 }}>
                    {record.employeeName}
                  </td>
                  <td>
                    {formatDate(record.payPeriodStart)} — {formatDate(record.payPeriodEnd)}
                  </td>
                  <td className="rate-value">{formatCurrency(record.grossPay)}</td>
                  <td style={{ color: 'var(--text-secondary)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {record.notes || '—'}
                  </td>
                  <td>
                    <div className="action-buttons" style={{ justifyContent: 'flex-end' }}>
                      <button
                        className="action-btn"
                        title="Edit"
                        onClick={() => handleOpenModal(record)}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        className="action-btn delete"
                        title="Delete"
                        onClick={() => handleDelete(record.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {payroll.length > 50 && (
          <p style={{ textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.8125rem', marginTop: '0.5rem' }}>
            Showing first 50 of {payroll.length} entries
          </p>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingRecord ? 'Edit Payroll Entry' : 'Add Payroll Entry'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Employee</label>
                  <select
                    value={formData.employeeId}
                    onChange={(e) => setFormData(prev => ({ ...prev, employeeId: e.target.value }))}
                    required
                    disabled={!!editingRecord}
                  >
                    <option value="">Select employee...</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name}</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>Period Start</label>
                    <input
                      type="date"
                      value={formData.payPeriodStart}
                      onChange={(e) => setFormData(prev => ({ ...prev, payPeriodStart: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Period End</label>
                    <input
                      type="date"
                      value={formData.payPeriodEnd}
                      onChange={(e) => setFormData(prev => ({ ...prev, payPeriodEnd: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Total Cost (Gross + Employer Taxes)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.grossPay}
                    onChange={(e) => setFormData(prev => ({ ...prev, grossPay: e.target.value }))}
                    placeholder="e.g., 2500.00"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Notes (optional)</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="e.g., Includes bonus, PTO payout, etc."
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={!formData.employeeId || !formData.grossPay || !formData.payPeriodStart || !formData.payPeriodEnd}
                >
                  {editingRecord ? 'Update' : 'Add'} Entry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Preview Modal */}
      {showImportModal && importPreview && (
        <div className="modal-overlay" onClick={() => !importing && setShowImportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h3>Import Payroll from CSV</h3>
              <button className="modal-close" onClick={() => !importing && setShowImportModal(false)} disabled={importing}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                Found <strong>{importPreview.totalRows}</strong> payroll entries to import.
                Preview of first {Math.min(10, importPreview.rows.length)} rows:
              </p>

              <table className="compensation-table" style={{ fontSize: '0.8125rem' }}>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Pay Period</th>
                    <th>Gross Pay</th>
                    <th>Employer Taxes</th>
                  </tr>
                </thead>
                <tbody>
                  {importPreview.rows.map((row, idx) => {
                    const parseMoney = (val) => parseFloat((val || '0').replace(/[$,]/g, '')) || 0;
                    const gross = parseMoney(row.grossPay);
                    const taxes = parseMoney(row.employerMedicareTax) + parseMoney(row.employerSocialSecurity) + parseMoney(row.stateUnemployment);
                    return (
                      <tr key={idx}>
                        <td>{row.employeeFirstName} {row.employeeLastName}</td>
                        <td>{row.payPeriod}</td>
                        <td>{formatCurrency(gross)}</td>
                        <td>{formatCurrency(taxes)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <p style={{ marginTop: '1rem', fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>
                Duplicates will be automatically skipped. Voided paychecks are excluded.
              </p>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={() => setShowImportModal(false)} disabled={importing}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-save"
                onClick={handleImport}
                disabled={importing}
              >
                {importing ? 'Importing...' : `Import ${importPreview.totalRows} Entries`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Modal */}
      {showQuickAdd && (
        <div className="modal-overlay" onClick={() => !quickAddSaving && setShowQuickAdd(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Quick Add Pay Period</h3>
              <button className="modal-close" onClick={() => !quickAddSaving && setShowQuickAdd(false)} disabled={quickAddSaving}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleQuickAddSubmit}>
              <div className="modal-body">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div className="form-group">
                    <label>Period Start</label>
                    <input
                      type="date"
                      value={quickAddData.payPeriodStart}
                      onChange={(e) => setQuickAddData(prev => ({ ...prev, payPeriodStart: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Period End</label>
                    <input
                      type="date"
                      value={quickAddData.payPeriodEnd}
                      onChange={(e) => setQuickAddData(prev => ({ ...prev, payPeriodEnd: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                  Enter total cost for each person. Leave blank to skip.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {employees.map(emp => (
                    <div key={emp.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <label style={{ flex: 1, fontWeight: 500, fontSize: '0.875rem' }}>
                        {emp.name}
                      </label>
                      <div style={{ position: 'relative', width: '140px' }}>
                        <span style={{
                          position: 'absolute',
                          left: '0.75rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: 'var(--text-tertiary)',
                          fontSize: '0.875rem'
                        }}>$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={quickAddData.amounts[emp.id] || ''}
                          onChange={(e) => handleQuickAddAmountChange(emp.id, e.target.value)}
                          style={{
                            width: '100%',
                            paddingLeft: '1.5rem',
                            textAlign: 'right'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowQuickAdd(false)} disabled={quickAddSaving}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={quickAddSaving || !quickAddData.payPeriodStart || !quickAddData.payPeriodEnd}
                >
                  {quickAddSaving ? 'Saving...' : 'Save All'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollAdmin;
